import { useEffect, useState } from 'react';
import { ArrowLeft, ChevronRight, CheckCircle2, Mail, PackageCheck, ShieldCheck } from 'lucide-react';
import { BrandLogo } from './BrandLogo';
import { ClubQuoteIntake } from './ClubQuoteIntake';
import { LegalFooter } from './LegalFooter';
import type { ExperimentVariant } from '../data/growthExperiments';
import { seoGuidePages, type SeoGuidePage } from '../data/seoPages';
import { getFaqsForPage } from '../data/faqContentBank';
import { trackEvent } from '../lib/analytics';
import { selectExperimentVariant } from '../lib/experiments';

type SeoLandingPageProps = {
  page: SeoGuidePage;
};

export function SeoLandingPage({ page }: SeoLandingPageProps) {
  const faqs = getFaqsForPage(page);
  const [ctaVariant, setCtaVariant] = useState<ExperimentVariant | null>(null);

  useEffect(() => {
    document.title = `${page.title} | ProPickleball.nl`;
    const description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (description) description.content = page.meta;
  }, [page]);

  useEffect(() => {
    let isActive = true;
    selectExperimentVariant('seo_guide_cta_v1').then((variant) => {
      if (isActive) setCtaVariant(variant);
    });
    return () => {
      isActive = false;
    };
  }, [page.slug]);

  const ctaHref = page.cluster === 'b2b-club-school' ? '#clubpakket-aanvraag' : (ctaVariant?.primaryHref ?? '/producten/');
  const ctaLabel = page.cluster === 'b2b-club-school' ? page.ctaLabel : (ctaVariant?.primaryCta ?? page.ctaLabel);

  return (
    <>
      <main className="seoPage">
        <nav className="legalTopbar" aria-label="Keuzehulp navigatie">
          <BrandLogo compact />
          <a className="backHome" href="/"><ArrowLeft size={18} aria-hidden="true" /> Terug naar de webshop</a>
        </nav>
        <nav className="seoBreadcrumbs" aria-label="Breadcrumb">
          <a href="/">Home</a>
          <ChevronRight size={15} aria-hidden="true" />
          <a href="/#gidsen">Keuzehulpen</a>
          <ChevronRight size={15} aria-hidden="true" />
          <span>{page.title}</span>
        </nav>
        <header className="seoHero">
          <p className="eyebrow">Keuzehulp & SEO</p>
          <h1>{page.title}</h1>
          <p>{page.intro}</p>
          <div className="seoMeta">
            <span>{page.keyword}</span>
            <span>{page.audience}</span>
            <span>{page.cluster}</span>
            <span>{page.buyerStage}</span>
          </div>
          <a
            className="primaryCta"
            href={ctaHref}
            onClick={() => trackEvent({ event: 'cta_click', location: page.slug, label: ctaLabel, routeFamily: 'seo_guide', placement: ctaVariant?.id ?? 'default' })}
          >
            {ctaLabel}
          </a>
        </header>
        <section className="section seoDecisionStrip" aria-label="Waarom deze route">
          <article>
            <CheckCircle2 aria-hidden="true" />
            <strong>Past bij</strong>
            <p>{page.audience}</p>
          </article>
          <article>
            <PackageCheck aria-hidden="true" />
            <strong>Koopfase</strong>
            <p>{page.buyerStage}</p>
          </article>
          <article>
            <ShieldCheck aria-hidden="true" />
            <strong>Belofte</strong>
            <p>Geen voorraad-, levertijd- of merkclaim zonder bewijs.</p>
          </article>
          <article>
            <Mail aria-hidden="true" />
            <strong>Vervolg</strong>
            <p>{page.cluster === 'b2b-club-school' ? 'Offerte-intake voor pakketadvies.' : 'Keuzehulp, productroute of beschikbaarheidsupdate.'}</p>
          </article>
        </section>
        <section className="section seoChecklist">
          <div>
            <p className="eyebrow">Beslispunten</p>
            <h2>Wat je eerst moet kiezen.</h2>
          </div>
          <div className="checklist">
            <ul>
              {page.checklist.map((item) => <li key={item}>{item}</li>)}
            </ul>
          </div>
        </section>
        <section className="section seoFaqSection">
          <div>
            <p className="eyebrow">Veelgestelde vragen</p>
            <h2>Antwoorden die matchen met de zoekintentie.</h2>
          </div>
          <div className="seoFaqList">
            {faqs.map((item) => (
              <article key={item.id ?? item.question} id={item.id ? `faq-${item.id}` : undefined}>
                <h3>{item.question}</h3>
                <p>{item.answer}</p>
              </article>
            ))}
          </div>
        </section>
        <section className="section seoInternalLinks" aria-label="Vervolgstappen">
          <div>
            <p className="eyebrow">Volgende stap</p>
            <h2>Ga door naar de juiste keuze- of productroute.</h2>
          </div>
          <div className="guideLinkGrid">
            {page.internalLinks.map((link) => (
              <a key={`${page.slug}-${link.href}`} href={link.href} onClick={() => trackEvent({ event: 'category_click', location: page.slug, label: link.label })}>
                <strong>{link.label}</strong>
                <span>{link.href}</span>
              </a>
            ))}
          </div>
        </section>
        <section className="section">
          <p className="eyebrow">Alle keuzehulpen</p>
          <h2>Gebruik de juiste route voor je vraag.</h2>
          <div className="guideLinkGrid">
            {seoGuidePages.map((guide) => (
              <a key={guide.slug} href={guide.slug} onClick={() => trackEvent({ event: 'category_click', location: page.slug, label: guide.keyword })}>
                <strong>{guide.title}</strong>
                <span>{guide.meta}</span>
              </a>
            ))}
          </div>
        </section>
        <div id="clubpakket-aanvraag">
          <ClubQuoteIntake source={page.slug} />
        </div>
      </main>
      <LegalFooter />
    </>
  );
}
